import { getLabelFromStorage, consoleEvents } from '@pb/utils'

import appSelectors from 'App/duck/selectors'
import { MESSAGE_CODE_TO_LABEL_KEY } from 'features/vouchers/constants'
import { postVouchers } from 'features/vouchers/requests'

import {
  setActiveOfferToLocalStorage,
  getRedeemExpireTime,
  hasExpiredOfferInLocalStorage,
  removeOfferFromStorage,
  getOfferIdFromLocalStorage,
} from '../helpers/localStorage'
import requests from '../requests'
import { OfferTracking, MergedOffers } from '../types'
import actions from './actions'
import { normalizeMatchingOffers, convertOfferDetailData, mergeRewards } from './normalize'
import selectors from './selectors'

export const operationCheckCleanActivatedOffer = () => (_dispatch, getState) => {
  const { localStorage } = window
  const customerLoyaltyId = appSelectors.getCustomerLoyaltyId(getState())
  const shouldClearLocalStorage = hasExpiredOfferInLocalStorage(localStorage, customerLoyaltyId)
  if (shouldClearLocalStorage) {
    removeOfferFromStorage(localStorage, customerLoyaltyId)
  }
}

const createOfferPropositionsValueTracking = (offerPropositions: any): any => {
  const offerKeys: string[] = offerPropositions.map((offer) => offer.offerKey).join(';')
  const offerTypes: string[] = offerPropositions.map((offer) => offer.offerType)
  const offerCountArray: String[] = []
  const count = {}

  offerTypes.forEach((type) => {
    if (count[type] == null) {
      count[type] = 1
    } else {
      count[type] += 1
    }
  })

  Object.keys(count).forEach((key) => {
    offerCountArray.push(key.concat(':').concat(count[key]))
  })

  const offerTypeString = offerCountArray.join(';')
  return [offerKeys, offerTypeString]
}

export const operationFetchRewardsStart =
  (setOfferTracking?: OfferTracking) => async (dispatch: any, getState: any) => {
    dispatch(actions.fetchRewardsStart())
    dispatch(operationCheckCleanActivatedOffer())

    const customerLoyaltyId = appSelectors.getCustomerLoyaltyId(getState())

    return (
      Promise.all([requests.fetchConfiguredOffers(), requests.fetchOffersPropositions()])
        .then(([configuredOffers, offerPropositions]) => {
          const normalizedRewards = normalizeMatchingOffers(
            configuredOffers,
            offerPropositions,
            customerLoyaltyId,
          )
          if (setOfferTracking) {
            const [offerKeyTracking, offerTypeTracking] =
              createOfferPropositionsValueTracking(offerPropositions)
            setOfferTracking(offerKeyTracking, offerTypeTracking)
          }
          // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
          dispatch(actions.fetchRewardsSuccess(normalizedRewards))
        })
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
        .catch((err) => dispatch(actions.fetchRewardsFail(err)))
    )
  }

export const operationFetchRewards =
  (setOfferTracking?: OfferTracking, isNewVouchersOffersEnabled = false) =>
  async (dispatch: any, getState: any) => {
    dispatch(actions.fetchRewardsStart())
    dispatch(operationCheckCleanActivatedOffer())

    const customerLoyaltyId = appSelectors.getCustomerLoyaltyId(getState())

    try {
      const offerPropositions = await requests.fetchOffersPropositions()
      const offerKeys = offerPropositions?.map((item) => item.offerKey)
      let normalizedRewards: MergedOffers[] = []
      if (offerKeys.length) {
        const memberOffers = await requests.fetchMemberOffers(offerKeys.join(','))
        normalizedRewards = isNewVouchersOffersEnabled
          ? mergeRewards(memberOffers, offerPropositions, customerLoyaltyId)
          : normalizeMatchingOffers(memberOffers, offerPropositions, customerLoyaltyId)
      }
      if (setOfferTracking) {
        const [offerKeyTracking, offerTypeTracking] =
          createOfferPropositionsValueTracking(offerPropositions)
        setOfferTracking(offerKeyTracking, offerTypeTracking)
      }
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
      dispatch(actions.fetchRewardsSuccess(normalizedRewards))
    } catch (error) {
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
      dispatch(actions.fetchRewardsFail(error))
    }
  }

export const operationFetchRewardDetail = (path) => async (dispatch) => {
  dispatch(actions.fetchRewardDetailStart())

  try {
    const { data } = await requests.fetchRewardDetail(path)
    const normalized = convertOfferDetailData(data)
    if (normalized) {
      dispatch(actions.fetchRewardDetailSuccess(normalized))
    } else {
      dispatch(
        actions.fetchRewardDetailFail(
          // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
          'Either Reward Detail response was empty, or normalization failed',
        ),
      )
    }
  } catch (error) {
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
    dispatch(actions.fetchRewardDetailFail(error))
  }
}

export const operationRedeemOffer =
  ({ data, redeemCountdown }) =>
  async (dispatch, getState) => {
    const state = getState()
    const customerLoyaltyId = appSelectors.getCustomerLoyaltyId(state)
    const offer = selectors.getRewardByOfferPropositionId(state, data?.offerPropositionId)
    const activeOfferExpiresAt = getRedeemExpireTime(redeemCountdown || 15)
    const { localStorage } = window

    dispatch(actions.redeemOfferStart())

    try {
      const { status, statusText } = await requests.redeemOffer(data)
      if (status === 200) {
        setActiveOfferToLocalStorage(localStorage, customerLoyaltyId, {
          ...offer,
          loyaltyOffersCounter: redeemCountdown,
        })
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
        dispatch(actions.redeemOfferSuccess({ ...offer, activeOfferExpiresAt }))
      } else {
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
        dispatch(actions.redeemOfferFail(statusText))
      }
    } catch (error) {
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
      dispatch(actions.redeemOfferFail(error))
    }
  }

export const operationAddOfferToBag = (data: any) => async (dispatch) => {
  dispatch(actions.addOfferToBagStart())

  try {
    await postVouchers([data])
    dispatch(actions.addOfferToBagSuccess())
  } catch (error) {
    const responseCode = error?.response?.data[0]?.code
    const errorCopy = responseCode && getLabelFromStorage(MESSAGE_CODE_TO_LABEL_KEY[responseCode])
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
    dispatch(actions.addOfferToBagFail(errorCopy || error?.message))
  }
}

export const operationCancelActiveOffer = () => (dispatch, getState) => {
  const { localStorage } = window
  const customerLoyaltyId = appSelectors.getCustomerLoyaltyId(getState())
  const currentlyActiveOffer = getOfferIdFromLocalStorage(localStorage, customerLoyaltyId)
  try {
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
    dispatch(actions.cancelActiveOffer(currentlyActiveOffer))
    removeOfferFromStorage(localStorage, customerLoyaltyId)
  } catch (error) {
    consoleEvents.error('Failed to cancel offer')
  }
}

export const operationfetchPromotionCode = (data) => async (dispatch) => {
  dispatch(actions.fetchPromotionCodeStart())
  try {
    const response = await requests.redeemOffer(data)
    if (response.status === 200) {
      dispatch(actions.fetchPromotionCodeSuccess(response?.data?.promotionCode))
    } else {
      dispatch(actions.fetchPromotionCodeFail(response.statusText))
    }
  } catch (error) {
    dispatch(actions.fetchPromotionCodeFail(error))
  }
}

export default {
  operationFetchRewardsStart,
  operationFetchRewards,
  operationFetchRewardDetail,
  operationRedeemOffer,
}
